import { HttpClient } from '../api/HttpClient';
import { API_ENDPOINTS } from '../api/ApiConfig';
import type {
  Genre,
  GenresData,
  GenresQueryParams,
  GenreCreateRequest,
  GenreUpdateRequest,
} from '../../types/api/genre';

export class GenreService {
  private httpClient: HttpClient;

  constructor() {
    this.httpClient = HttpClient.getInstance();
  }

  async getPublicGenres(params: GenresQueryParams = {}): Promise<GenresData> {
    const query = new URLSearchParams();
    query.append('page', (params.page ?? 0).toString());
    query.append('size', (params.size ?? 10).toString());
    const response = await this.httpClient.get<GenresData>(
      `${API_ENDPOINTS.GENRES.ADMIN_LIST}?${query.toString()}`
    );
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch genres');
    }
    return response.data;
  }

  async createGenre(payload: { data: GenreCreateRequest; iconFile?: File | Blob | null }): Promise<Genre> {
    const formData = this.buildGenreFormData(payload.data, payload.iconFile);
    const response = await this.httpClient.post<Genre>(`/admin/genres`, formData);
    if (!response.success) {
      throw new Error(response.message || 'Failed to create genre');
    }
    return response.data;
  }

  async updateGenre(
    genreId: string,
    payload: { data: GenreUpdateRequest; iconFile?: File | Blob | null }
  ): Promise<Genre> {
    const formData = this.buildGenreFormData(payload.data, payload.iconFile);
    const response = await this.httpClient.put<Genre>(`/admin/genres/${genreId}`, formData);
    if (!response.success) {
      throw new Error(response.message || 'Failed to update genre');
    }
    return response.data;
  }

  async deleteGenre(genreId: string): Promise<void> {
    const response = await this.httpClient.delete<{
      success: boolean;
      message: string;
      timestamp: string;
    }>(`/admin/genres/${genreId}`);
    if (!response.success) {
      throw new Error(response.message || 'Failed to delete genre');
    }
  }

  private buildGenreFormData(
    data: GenreCreateRequest | GenreUpdateRequest,
    iconFile?: File | Blob | null
  ): FormData {
    const formData = new FormData();
    const dataBlob = new Blob([JSON.stringify(data)], { type: 'application/json' });
    formData.append('data', dataBlob, 'data.json');

    if (iconFile) {
      const fileName =
        iconFile instanceof File && iconFile.name
          ? iconFile.name
          : (iconFile as any)?.name || 'icon';
      formData.append('icon', iconFile, fileName);
    }
    return formData;
  }
}
